import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  collectFriendAnalytics,
  getFriendAnalyticsDaily,
  getFriendAnalyticsOverview,
} from 'api/friendAnalytics';
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context';
import { queryKeys } from 'constants/queryKeys';
import { friendAnalyticsStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type {
  TFriendAnalyticsCollectResult,
  TFriendAnalyticsDaily,
  TFriendAnalyticsOverview,
} from 'types/friendAnalytics';
import { toErrorMessage } from './utils';

/** LINE集計は日次のため自動再取得しない。併記するユーザー数も画面の読み込み・再読み込み時点の値。 */
const manualRefreshQueryOptions = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

type UseFriendAnalyticsOverviewResult = {
  data: TFriendAnalyticsOverview | undefined;
  loading: boolean;
  isRefetching: boolean;
  error: string | undefined;
  refetch: () => void;
};

export function useFriendAnalyticsOverview(): UseFriendAnalyticsOverviewResult {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [queryKeys.FRIEND_ANALYTICS, 'overview', user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: getFriendAnalyticsOverview,
    ...manualRefreshQueryOptions,
    retry: false,
  });

  return {
    data: query.data,
    loading: Boolean(user?.uid) && query.isPending,
    isRefetching: query.isRefetching,
    error: query.error
      ? toErrorMessage(query.error, friendAnalyticsStrings.errors.loadFailed)
      : undefined,
    refetch: () => {
      void query.refetch();
    },
  };
}

type UseFriendAnalyticsDailyOptions = {
  from: string;
  to: string;
};

type UseFriendAnalyticsDailyResult = {
  data: TFriendAnalyticsDaily | undefined;
  loading: boolean;
  isRefetching: boolean;
  error: string | undefined;
  refetch: () => void;
};

export function useFriendAnalyticsDaily(
  options: UseFriendAnalyticsDailyOptions,
): UseFriendAnalyticsDailyResult {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [
      queryKeys.FRIEND_ANALYTICS,
      'daily',
      user?.uid,
      options.from,
      options.to,
    ],
    enabled: Boolean(user?.uid && options.from && options.to),
    queryFn: () =>
      getFriendAnalyticsDaily({ from: options.from, to: options.to }),
    ...manualRefreshQueryOptions,
    retry: false,
  });

  return {
    data: query.data,
    loading: Boolean(user?.uid && options.from && options.to) && query.isPending,
    isRefetching: query.isRefetching,
    error: query.error
      ? toErrorMessage(query.error, friendAnalyticsStrings.errors.loadFailed)
      : undefined,
    refetch: () => {
      void query.refetch();
    },
  };
}

type UseCollectFriendAnalyticsResult = {
  collect: () => void;
  isCollecting: boolean;
  result: TFriendAnalyticsCollectResult | undefined;
  error: string | undefined;
};

/**
 * LINEから自組織の最新データを今すぐ取りに行く。
 * 毎時の自動取得を待たずに見たいときの経路で、終わったら概要・日次を取り直す。
 */
export function useCollectFriendAnalytics(): UseCollectFriendAnalyticsResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const mutationKey = [
    queryKeys.FRIEND_ANALYTICS_COLLECT,
    user?.uid,
    organizationId,
  ];
  const isCollecting = useIsMutating({ mutationKey, exact: true }) > 0;

  const mutation = useMutation({
    mutationKey,
    mutationFn: collectFriendAnalytics,
    onSuccess: async () => {
      // 取り切れていなくても、保存できた分は画面へ反映する
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.FRIEND_ANALYTICS],
      });
    },
    retry: false,
  });

  return {
    collect: () => {
      // ページへ戻った場合や連続クリックでも、同じユーザーの取得を重ねない。
      if (queryClient.isMutating({ mutationKey, exact: true }) === 0) {
        mutation.mutate();
      }
    },
    isCollecting,
    result: mutation.data,
    error: mutation.error
      ? toErrorMessage(mutation.error, friendAnalyticsStrings.errors.collectFailed)
      : undefined,
  };
}
